import { z } from 'zod';
import { getViewerGrantId } from '../auth/store';
import { ensureFreshApiToken, isMsalConfigured } from '../auth/msal';
import { useAuth } from '../auth/store';

/**
 * Thin typed fetch wrapper around the Fastify API. Every call:
 *
 *   1. Targets a relative URL (the Vite dev proxy + same-origin prod mean
 *      we don't need a base URL here).
 *   2. Attaches the bearer token if one is in the auth store.
 *   3. Stamps an `X-Correlation-Id` header per ADR conventions so requests
 *      are traceable end-to-end through the API + workers.
 *   4. Parses the response body as JSON and runs it through a Zod schema
 *      when provided; this is the strict-type boundary between the
 *      frontend and the backend. A surprise shape surfaces as an
 *      `ApiError` rather than a runtime crash deeper in the UI tree.
 *
 * The error taxonomy mirrors the API's:
 *   - `validation` — 400
 *   - `authentication` — 401
 *   - `authorization` — 403
 *   - `not_found` — 404
 *   - `conflict` — 409
 *   - `rate_limited` — 429
 *   - `dependency_failure` — 502/503
 *   - `internal` — 500 fallback
 */

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown> | undefined;
  readonly correlationId: string;

  constructor(
    status: number,
    code: string,
    message: string,
    correlationId: string,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.correlationId = correlationId;
    this.details = details;
  }
}

const ApiErrorBodySchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.unknown()).optional(),
  }),
});

export interface ApiClientOptions<TBody = unknown> {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: TBody;
  /**
   * Optional query string params. Values are stringified; arrays repeat the key.
   * `undefined` values are omitted so callers can spread optional filters.
   */
  query?: Record<string, string | number | boolean | undefined>;
  /** Per-call header overrides. The base headers (auth, content-type) merge underneath. */
  headers?: Record<string, string>;
  /** Optional Idempotency-Key. Mutating endpoints honor this per cross-cutting §2. */
  idempotencyKey?: string;
  /** Skip the bearer-header attach (e.g. magic-link redeem is public). */
  noAuth?: boolean;
  /**
   * When set, the X-Viewer-Grant-Id header is attached in place of the
   * bearer. Used by the client-viewer surfaces (chat, etc.). The viewer
   * grant must already be persisted in the auth store; if not present,
   * the header is silently omitted.
   */
  asViewer?: boolean;
  /** Override the AbortSignal that powers `fetch` cancellation. */
  signal?: AbortSignal;
}

/**
 * Generate a per-request correlation id. v1 uses a short random string —
 * `crypto.randomUUID()` is available in every browser we target (Chrome,
 * Edge, Safari 15.4+, Firefox 95+).
 */
function newCorrelationId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return `cid-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function buildQueryString(query?: ApiClientOptions['query']): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) continue;
    params.set(key, String(value));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

/** A request ready to send: the URL, every header, and its correlation id. */
export interface PreparedRequest {
  url: string;
  headers: Record<string, string>;
  correlationId: string;
}

/**
 * Build the URL and the headers of a request: correlation id, auth (bearer
 * or viewer grant), Idempotency-Key and the JSON content type. Shared by
 * `apiClient` and the XHR upload helper (`./upload`) so both send the same
 * request.
 */
export async function prepareApiRequest(
  path: string,
  options: ApiClientOptions = {},
): Promise<PreparedRequest> {
  const correlationId = newCorrelationId();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Correlation-Id': correlationId,
    ...(options.headers ?? {}),
  };

  if (options.body !== undefined && !(options.body instanceof Uint8Array)) {
    headers['Content-Type'] = headers['Content-Type'] ?? 'application/json';
  }

  if (!options.noAuth) {
    if (options.asViewer) {
      const grant = getViewerGrantId();
      if (grant) headers['X-Viewer-Grant-Id'] = grant;
    } else {
      // MSAL returns a cached token and auto-refreshes near expiry; falls back
      // to the stored token for the dev-auth / magic-link paths.
      const token = await ensureFreshApiToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    }
  }

  if (options.idempotencyKey) {
    headers['Idempotency-Key'] = options.idempotencyKey;
  }

  return { url: path + buildQueryString(options.query), headers, correlationId };
}

/** What `readApiResponse` needs of a response; a fetch `Response` is one. */
export interface ApiResponseLike {
  status: number;
  ok: boolean;
  json(): Promise<unknown>;
}

/**
 * Core fetch helper. Throws `ApiError` on non-2xx and on schema-validation
 * failures; the latter is treated as 500 so the UI can surface "we got an
 * unexpected response from the server" without crashing.
 */
export async function apiClient<TResp>(
  path: string,
  schema: z.ZodType<TResp>,
  options: ApiClientOptions = {},
): Promise<TResp> {
  const { url, headers, correlationId } = await prepareApiRequest(path, options);

  let res: Response;
  try {
    let body: BodyInit | undefined;
    if (options.body === undefined) {
      body = undefined;
    } else if (options.body instanceof Uint8Array) {
      // BodyInit accepts ArrayBuffer / Blob — cast through ArrayBufferView
      // to keep TS happy while preserving the typed-array runtime.
      body = options.body as unknown as BodyInit;
    } else {
      body = JSON.stringify(options.body);
    }
    res = await fetch(url, {
      method: options.method ?? 'GET',
      headers,
      body,
      signal: options.signal,
    });
  } catch (err) {
    // Network-layer failure — surface as dependency_failure so the UI can
    // distinguish from API-reported errors.
    throw new ApiError(
      0,
      'network',
      err instanceof Error ? err.message : 'Network error.',
      correlationId,
    );
  }

  return readApiResponse(res, schema, path, correlationId);
}

/**
 * Turn an answered request into the parsed body or an `ApiError`: the
 * error envelope, the 401 recovery and the schema check. Shared by
 * `apiClient` and the XHR upload helper.
 */
export async function readApiResponse<TResp>(
  res: ApiResponseLike,
  schema: z.ZodType<TResp>,
  path: string,
  correlationId: string,
): Promise<TResp> {
  if (res.status === 204) {
    // No-content responses — schema must accept undefined.
    return schema.parse(undefined);
  }

  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    if (!res.ok) {
      throw new ApiError(
        res.status,
        'internal',
        `Request failed with status ${res.status} and a non-JSON body.`,
        correlationId,
      );
    }
    // Some endpoints (rare) return 200 with empty bodies — the schema must
    // accept this case explicitly.
    return schema.parse(undefined);
  }

  if (!res.ok) {
    // A 401 means the bearer is dead (expired / revoked) — not a data
    // error. Recover by clearing the session and routing to sign-in with
    // a return path, instead of stranding the user on a broken workbench
    // with a red banner. Guarded to the MSAL path; dev/viewer sessions
    // keep the inline error. A secure-link route's 401 is about the link,
    // not a session: the client page says so itself (T-0191).
    if (
      res.status === 401 &&
      isMsalConfigured() &&
      !window.location.pathname.startsWith('/login') &&
      !path.startsWith('/api/v1/secure-links/')
    ) {
      useAuth.getState().clear();
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.assign(`/login?next=${next}`);
    }
    const parsed = ApiErrorBodySchema.safeParse(payload);
    if (parsed.success) {
      throw new ApiError(
        res.status,
        parsed.data.error.code,
        parsed.data.error.message,
        correlationId,
        parsed.data.error.details,
      );
    }
    throw new ApiError(
      res.status,
      'internal',
      `Request failed with status ${res.status}.`,
      correlationId,
    );
  }

  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error('API response failed schema validation', {
        path,
        correlationId,
        issues: parsed.error.issues,
        payload,
      });
    }
    throw new ApiError(
      500,
      'schema_mismatch',
      'The API returned an unexpected response shape.',
      correlationId,
      { issues: parsed.error.issues as unknown as Record<string, unknown> },
    );
  }
  return parsed.data;
}
